import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { api, loadRazorpay, formatINR, monthLabel } from "../services/mmcapi";
import AdvanceMaintenanceModal from "../Screens/Advancemaintenancemodal";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};


const isAdvanceType = (name) =>
  /^advance\s+maint/i.test(String(name || "").trim());

const STATUS_LABELS = {
  pending: "Pending",
  overdue: "Overdue",
  paid: "Paid",
  cancelled: "Cancelled",
};

const effectiveStatus = (d) => {
  if (d?.status === "pending" && d?.isOverdue) return "overdue";
  return d?.status || "pending";
};

const statusBadgeStyle = (s) => {
  if (s === "paid")
    return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  if (s === "overdue")
    return { background: "rgba(239,68,68,0.18)", color: "#dc2626" };
  if (s === "cancelled")
    return { background: "rgba(107,114,128,0.15)", color: "#4b5563" };
  return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
};

const MaintenanceDues = () => {
  const [dues, setDues] = useState([]);
  const [summary, setSummary] = useState({
    pendingCount: 0,
    pendingTotal: 0,
    overdueCount: 0,
  });
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selected, setSelected] = useState(null); 
  const [paying, setPaying] = useState(false);

  const role = useMemo(
    () =>
      String(localStorage.getItem("role") || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, ""),
    [],
  );
  const isMember = role === "OWNER" || role === "TENANT";
  const [chargeTypes, setChargeTypes] = useState([]);
  const [showAdvance, setShowAdvance] = useState(false);

  useEffect(() => {
    if (!isMember) return;
    (async () => {
      try {
        const data = await api(
          "paymentChargeType.getActive",
          {},
          { silent: true },
        );
        setChargeTypes(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        setChargeTypes([]);
      }
    })();
  }, [isMember]);

  const onChargeTypeChange = (e) => {
    const type = chargeTypes.find((t) => String(t.id) === e.target.value);
    if (!type) return;
    if (isAdvanceType(type.name)) {
      setShowAdvance(true);
    } else {
      toast.info(`Payment for "${type.name}" is not available yet`);
    }
  };

  const load = useCallback(async () => {
    try {
      const data = await api(
        "mmc.getMyDues",
        status === "all" ? {} : { status },
      );
      setDues(Array.isArray(data.dues) ? data.dues : []);
      setSummary(
        data.summary || { pendingCount: 0, pendingTotal: 0, overdueCount: 0 },
      );
    } catch (e) {
      toast.error(e.message);
      setDues([]);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const total = dues.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const rows = useMemo(
    () => dues.slice((currentPage - 1) * limit, currentPage * limit),
    [dues, currentPage, limit],
  );

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  const closeModal = () => {
    if (paying) return;
    setSelected(null);
  };

  const proceedToPay = async () => {
    if (!selected || paying) return;
    setPaying(true);
    try {
      await loadRazorpay();
      const order = await api("mmc.createOrder", {
        chargeId: selected.chargeId,
      });

      const rzp = new window.Razorpay({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: "Adukkumadi",
        description: `Maintenance - ${monthLabel(order.month)}`,
        theme: { color: "#007bff" },
        handler: async (resp) => {
          try {
            await api("mmc.verifyPayment", resp);
            toast.success("Payment successful");
          } catch (e) {
            toast.warning(
              "Payment received. Confirmation is in progress, refresh in a moment.",
            );
          }
          setSelected(null);
          setPaying(false);
          load();
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(
          r?.error?.description || "Payment failed. Please try again.",
        );
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.message);
      setPaying(false);
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Maintenance Dues</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Pay your monthly maintenance charges.
            </p>
          </div>

          {isMember ? (
            <div style={{ width: 230 }}>
              <select
                className="form-control"
                value=""
                onChange={onChargeTypeChange}
                aria-label="Payment Charge Type"
              >
                <option value="">Payment Charge Type</option>
                {chargeTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div
            className="d-flex flex-wrap"
            style={{ gap: 12, marginBottom: 12 }}
          >
            <div
              className="card mb-0"
              style={{
                borderLeft: "3px solid #f59930",
                width: 190,
                minHeight: 90,
              }}
            >
              <div
                className="card-body d-flex flex-column justify-content-center"
                style={{ padding: "16px 14px" }}
              >
                <div
                  className="text-muted"
                  style={{ fontSize: 12, lineHeight: 1.3 }}
                >
                  Total Pending
                </div>
                <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
                  {formatINR(summary.pendingTotal)}
                </div>
              </div>
            </div>

            <div
              className="card mb-0"
              style={{
                borderLeft: "3px solid #2563eb",
                width: 190,
                minHeight: 90,
              }}
            >
              <div
                className="card-body d-flex flex-column justify-content-center"
                style={{ padding: "16px 14px" }}
              >
                <div
                  className="text-muted"
                  style={{ fontSize: 12, lineHeight: 1.3 }}
                >
                  Pending Months
                </div>
                <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
                  {summary.pendingCount}
                </div>
              </div>
            </div>

            <div
              className="card mb-0"
              style={{
                borderLeft: "3px solid #dc2626",
                width: 190,
                minHeight: 90,
              }}
            >
              <div
                className="card-body d-flex flex-column justify-content-center"
                style={{ padding: "16px 14px" }}
              >
                <div
                  className="text-muted"
                  style={{ fontSize: 12, lineHeight: 1.3 }}
                >
                  Overdue
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    color: summary.overdueCount ? "#dc2626" : undefined,
                  }}
                >
                  {summary.overdueCount}
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div
                  className="role-table-top d-flex flex-wrap align-items-end justify-content-between"
                  style={{ gap: 12 }}
                >
                  <div className="form-group mb-0" style={{ width: 160 }}>
                    <label className="form-label" style={{ fontSize: 12 }}>
                      Status
                    </label>
                    <select
                      className="form-control"
                      style={{ width: "100%" }}
                      value={status}
                      onChange={(e) => {
                        setPage(1);
                        setStatus(e.target.value);
                      }}
                    >
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>

                  {summary.pendingCount > 1 ? (
                    <div
                      className="alert alert-info py-2 mb-3"
                      style={{ fontSize: 13 }}
                    >
                      Dues must be paid oldest first. Next to pay:{" "}
                      <strong>{monthLabel(summary.nextDueMonth)}</strong>
                    </div>
                  ) : null}
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>
                          <span className="role-th">Sl No.</span>
                        </th>
                        <th>
                          <span className="role-th">Month</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Flat</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Amount</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Due Date</span>
                        </th>
                        <th style={{ width: 130 }}>
                          <span className="role-th">Status</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Paid On</span>
                        </th>
                        <th style={{ width: 140 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? (
                        rows.map((d, idx) => {
                          const st = effectiveStatus(d);
                          return (
                            <tr key={String(d.chargeId || `row-${idx}`)}>
                              <td>{(currentPage - 1) * limit + idx + 1}</td>
                              <td>{monthLabel(d.month)}</td>
                              <td>
                                {[d.block, d.flatNumber]
                                  .filter(Boolean)
                                  .join("-") || "—"}
                              </td>
                              <td>{formatINR(d.amount)}</td>
                              <td>{formatDate(d.dueDate)}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    padding: "0.35rem 0.65rem",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    ...statusBadgeStyle(st),
                                  }}
                                >
                                  {(STATUS_LABELS[st] || st).toUpperCase()}
                                </span>
                              </td>
                              <td>{formatDate(d.paidAt)}</td>
                              <td>
                                {d.status === "pending" ? (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={!d.canPay}
                                    title={
                                      d.canPay
                                        ? ""
                                        : "Pay the earlier due first"
                                    }
                                    onClick={() => setSelected(d)}
                                  >
                                    Pay {formatINR(d.amount)}
                                  </button>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            No maintenance dues
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className="role-table-footer d-flex justify-content-between align-items-center flex-wrap"
                  style={{ gap: 10 }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 10 }}
                  >
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Show:
                    </span>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Total: {total}
                    </span>
                  </div>

                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(1)}
                    >
                      &laquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      &lsaquo;
                    </button>
                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      &rsaquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selected ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={closeModal}
        >
          <div
            className="card app-modal-card"
            style={{
              width: "min(420px, 96vw)",
              maxHeight: "90vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">Confirm payment</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeModal}
                disabled={paying}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="card-body">
              <div className="text-muted">Maintenance charge for</div>
              <div className="mb-2">
                <strong>{monthLabel(selected.month)}</strong>
              </div>
              <h2 className="my-3">{formatINR(selected.amount)}</h2>
              {selected.isOverdue ? (
                <p className="text-danger mb-0">
                  This due is past its due date.
                </p>
              ) : null}
            </div>

            <div
              className="card-footer d-flex justify-content-end"
              style={{ gap: 10 }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                disabled={paying}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={paying}
                onClick={proceedToPay}
              >
                {paying ? "Processing..." : "Proceed to Pay"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showAdvance ? (
        <AdvanceMaintenanceModal
          onClose={() => setShowAdvance(false)}
          onPaid={() => {
            setShowAdvance(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
};

export default MaintenanceDues;
